import { useEffect, useId, useRef } from 'react';
import { InputChevron } from './InputChevron';
import { InputCloseButton } from './InputCloseButton';
import { SparkIcon } from '../SparkIcon';
import { Label } from '../Label/Label';
import type { InputSingleSelectProps } from './InputSingleSelect.types';
import './shared/inputSelect.css';

export function InputSingleSelect({
  label = 'Label',
  placeholder = 'Input',
  text,
  value,
  onSelect: _onSelect,
  size = 'medium',
  fill,
  showLabel = true,
  showAsterisk = false,
  showHelpIcon = false,
  showPrefixIcon = false,
  prefixIcon,
  disabled = false,
  open = false,
  visualState,
  showFocus,
  onToggle,
  showClearButton = false,
  onClear,
  className = '',
  editable = false,
  searchValue,
  onSearchChange,
  onSearchKeyDown,
  activeDescendantId,
  onFieldKeyDown,
}: InputSingleSelectProps) {
  const labelId = useId();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const wasSearchShown = useRef(false);
  const resolvedState = disabled ? 'disabled' : visualState ?? 'default';
  const isFocused = showFocus ?? resolvedState === 'pressed';
  const effectiveText = value ?? text;
  const displayText = effectiveText ?? placeholder;
  const resolvedFill = fill ?? (effectiveText !== undefined && effectiveText.length > 0 ? 'filled' : 'empty');
  const isInteractive = Boolean(onToggle) && !disabled;
  const showSearchInput = editable && open && isInteractive;
  const showClear =
    showClearButton && !disabled && effectiveText !== undefined && effectiveText.length > 0;

  useEffect(() => {
    if (showSearchInput) {
      searchInputRef.current?.focus();
    } else if (wasSearchShown.current && document.activeElement === document.body) {
      // Closing the panel unmounts the focused search input, which drops
      // focus on <body> — the next Tab would restart from the page top.
      // Return focus to the field so Tab continues from this control.
      fieldRef.current?.focus();
    }
    wasSearchShown.current = showSearchInput;
  }, [showSearchInput]);

  const rootClasses = [
    'spark-input-select',
    `spark-input-select-${size}`,
    `spark-input-select-fill-${resolvedFill}`,
    `spark-input-select-state-${resolvedState}`,
    disabled ? 'spark-input-select-disabled' : '',
    isFocused ? 'spark-input-select-show-focus' : '',
    isInteractive ? 'spark-input-select-interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleFieldClick = () => {
    if (isInteractive) {
      onToggle?.();
    }
  };

  return (
    <div className={rootClasses}>
      {showLabel ? (
        <Label
          id={labelId}
          label={label}
          showAsterisk={showAsterisk}
          showIcon={showHelpIcon}
        />
      ) : null}
      <div className="spark-input-select-field-wrap">
        <div
          ref={fieldRef}
          className="spark-input-select-field"
          role="combobox"
          aria-labelledby={showLabel ? labelId : undefined}
          aria-disabled={disabled || undefined}
          aria-expanded={isInteractive ? open : undefined}
          aria-activedescendant={showSearchInput ? undefined : activeDescendantId}
          tabIndex={isInteractive ? 0 : undefined}
          onClick={handleFieldClick}
          onKeyDown={(event) => {
            if (!isInteractive) {
              return;
            }
            // While the editable search input is shown, keys belong to it.
            if (showSearchInput) {
              return;
            }
            // Parent-owned list navigation (arrows / Enter on a highlighted
            // option) runs first and suppresses the toggle by preventDefault.
            onFieldKeyDown?.(event);
            if (event.defaultPrevented) {
              return;
            }
            if (
              event.key === 'Enter' ||
              event.key === ' ' ||
              (editable && event.key === 'ArrowDown' && !open)
            ) {
              event.preventDefault();
              onToggle?.();
            }
          }}
        >
          {prefixIcon ? (
            <span className="spark-input-select-prefix">{prefixIcon}</span>
          ) : showPrefixIcon ? (
            <SparkIcon slug="user" size="small" className="spark-input-select-prefix" />
          ) : null}
          {showSearchInput ? (
            <input
              ref={searchInputRef}
              type="text"
              className="spark-input-select-search-input"
              value={searchValue ?? ''}
              // Current selection stays visible as the placeholder while filtering.
              placeholder={displayText}
              aria-autocomplete="list"
              aria-activedescendant={activeDescendantId}
              onChange={(event) => onSearchChange?.(event.target.value)}
              onKeyDown={onSearchKeyDown}
              onClick={(event) => {
                // Clicking into the input must not toggle the panel closed.
                event.stopPropagation();
              }}
            />
          ) : (
            <span className="spark-input-select-value">{displayText}</span>
          )}
          {showClear ? (
            <InputCloseButton
              className="spark-input-select-clear"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onClear?.();
              }}
            />
          ) : null}
          <InputChevron open={open} disabled={disabled} className="spark-input-select-chevron" />
        </div>
      </div>
    </div>
  );
}
